import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaArrowRight, FaCheckCircle, FaSearch } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';
import SouthBrisbaneMap from '../../components/SouthBrisbaneMap/SouthBrisbaneMap';

const coverageSuburbs = [
  'Annerley',
  'Archerfield',
  'Balmoral',
  'Bardon',
  'Burbank',
  'Camp Hill',
  'Carina',
  'Carina Heights',
  'Carindale',
  'Chelmer',
  'Clayfield',
  'Coopers Plains',
  'Coorparoo',
  'Darra',
  'Doolandella',
  'Dutton Park',
  'East Brisbane',
  'Eight Mile Plains',
  'Ellen Grove',
  'Fairfield',
  'Forest Lake',
  'Greenslopes',
  'Graceville',
  'Holland Park',
  'Holland Park West',
  'Inala',
  'Jamboree Heights',
  'Kangaroo Point',
  'Kenmore',
  'Kenmore Hills',
  'Kuraby',
  'Larapinta',
  'MacGregor',
  'Mackenzie',
  'Mansfield',
  'Moorooka',
  'Mount Gravatt',
  'Mount Gravatt East',
  'Nathan',
  'Oxley',
  'Pallara',
  'Parkinson',
  'Red Hill',
  'Richlands',
  'Robertson',
  'Rochedale',
  'Rochedale South',
  'Rocklea',
  'Runcorn',
  'Salisbury',
  'Seventeen Mile Rocks',
  'Sherwood',
  'Brisbane',
  'Spring Hill',
  'St Lucia',
  'Sumner',
  'Sunnybank',
  'Sunnybank Hills',
  'Taringa',
  'Tarragindi',
  'Tennyson',
  'Upper Mount Gravatt',
  'West End',
  'Wishart',
  'Woolloongabba',
  'Yeronga',
];

const coverageNotes = [
  {
    title: 'Same-Day Availability',
    description: 'Book today, and we can be at your doorstep the same day in most Brisbane suburbs.',
  },
  {
    title: 'No Travel Surcharge',
    description: 'We do not charge extra travel fees for suburbs within our Brisbane service area.',
  },
  {
    title: 'Local Teams',
    description: 'We have teams positioned across Brisbane for faster response times.',
  },
];

const Locations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllSuburbsMobile, setShowAllSuburbsMobile] = useState(false);

  const MOBILE_SUBURB_PREVIEW_COUNT = 12;

  const filteredSuburbs = coverageSuburbs.filter((suburb) =>
    suburb.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const isSearchActive = searchTerm.trim().length > 0;
  const shouldCollapseMobileSuburbs = !isSearchActive && !showAllSuburbsMobile;
  const mobileVisibleSuburbs = shouldCollapseMobileSuburbs
    ? filteredSuburbs.slice(0, MOBILE_SUBURB_PREVIEW_COUNT)
    : filteredSuburbs;

  return (
    <div>
      <Seo
        title="Service Areas"
        description="See the Brisbane suburbs and surrounding areas served by The Skilled Group."
        canonicalPath="/locations"
      />
      <div className="page-header">
        <div className="container-custom">
          <h1>Service Locations</h1>
          <p>
            The Skilled Group proudly serves homes and businesses across Brisbane and the suburbs listed below.
          </p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaMapMarkerAlt className="text-3xl text-primary-500" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-gray-800 mb-4">
              Serving Brisbane and Surrounding Suburbs
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed">
              No matter where you are in Brisbane, our team can reach you. We cover the suburbs below and can confirm nearby areas on request.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            {coverageNotes.map((point) => (
              <div
                key={point.title}
                className="bg-primary-50 rounded-xl p-6 border border-primary-100 text-center"
              >
                <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">
                  {point.title}
                </h3>
                <p className="text-gray-600 text-sm">{point.description}</p>
              </div>
            ))}
          </div>

          <div className="max-w-6xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FaCheckCircle className="text-primary-500" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-gray-800">
                Brisbane Coverage
              </h3>
            </div>
            <p className="text-gray-500 mb-6">
              We service the following suburbs across Brisbane:
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="relative mb-6 max-w-md">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setShowAllSuburbsMobile(false);
                    }}
                    placeholder="Search suburbs"
                    aria-label="Search Brisbane suburbs"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-11 pr-4 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                  />
                </div>

                {/* Count (desktop/tablet) */}
                <p className="hidden sm:block text-xs text-gray-400 mb-4">
                  {filteredSuburbs.length} suburb{filteredSuburbs.length === 1 ? '' : 's'} shown
                </p>

                {/* Count (mobile) */}
                <p className="sm:hidden text-xs text-gray-400 mb-4">
                  {mobileVisibleSuburbs.length} of {filteredSuburbs.length} suburb
                  {filteredSuburbs.length === 1 ? '' : 's'} shown
                </p>

                {/* Suburbs list (desktop/tablet) */}
                <div className="hidden sm:flex flex-wrap gap-2">
                  {filteredSuburbs.length > 0 ? (
                    filteredSuburbs.map((suburb) => (
                      <span
                        key={suburb}
                        className="bg-gray-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-100"
                      >
                        {suburb}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No suburbs match your search.</p>
                  )}
                </div>

                {/* Suburbs list (mobile collapsed/expanded) */}
                <div className="sm:hidden flex flex-wrap gap-2">
                  {filteredSuburbs.length > 0 ? (
                    mobileVisibleSuburbs.map((suburb) => (
                      <span
                        key={suburb}
                        className="bg-gray-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-100"
                      >
                        {suburb}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No suburbs match your search.</p>
                  )}
                </div>

                {/* View all (mobile only) */}
                {!isSearchActive && filteredSuburbs.length > MOBILE_SUBURB_PREVIEW_COUNT && (
                  <div className="sm:hidden mt-4">
                    <button
                      type="button"
                      onClick={() => setShowAllSuburbsMobile((value) => !value)}
                      className="btn-primary text-sm py-2.5 px-6 inline-flex items-center justify-center"
                    >
                      {showAllSuburbsMobile ? 'Show fewer suburbs' : 'View all suburbs'}
                    </button>
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
                <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                  <SouthBrisbaneMap />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-primary-500 py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Need Help Confirming Coverage?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            If your suburb is nearby and not listed, contact us and we will confirm whether we can service your location.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-accent inline-flex items-center justify-center gap-2"
            >
              Contact Us
              <FaArrowRight className="text-sm" />
            </Link>
            <Link
              to="/booking"
              className="btn-outline border-white text-white hover:bg-white hover:text-primary-500 inline-flex items-center justify-center gap-2"
            >
              Book Now
              <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Locations;